import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  messageBox: {
    padding: theme.spacing(1.5),
    borderRadius: 10,
    background: theme.mode === "light" ? "#f6f8fb" : "#16202a",
    border: `1px solid ${theme.mode === "light" ? "#d9e3ef" : "#25313d"}`,
    marginBottom: theme.spacing(2)
  },
  noteRow: {
    border: `1px solid ${theme.mode === "light" ? "#dde7f1" : "#22303a"}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    background: theme.mode === "light" ? "#fff" : "#18232c"
  },
  noteHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1)
  },
  noteMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center"
  },
  noteReply: {
    marginLeft: theme.spacing(3),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(1.5)
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 220px",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  actionsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  emptyState: {
    opacity: 0.8,
    padding: theme.spacing(1, 0)
  }
}));

const flattenNotes = (notes = [], level = 0) => {
  const rows = [];
  notes.forEach(note => {
    rows.push({ ...note, level });
    if (Array.isArray(note.replies) && note.replies.length) {
      rows.push(...flattenNotes(note.replies, level + 1));
    }
  });
  return rows;
};

const getMessagePreview = message => {
  if (!message) return "";
  if (message.body && String(message.body).trim()) return String(message.body).trim();
  if (message.mediaType) return `Mensaje de tipo ${message.mediaType}`;
  return "Mensaje sin contenido de texto";
};

const MessageCoachNotesDialog = ({
  open,
  onClose,
  message,
  ticket,
  currentUser,
  onChanged
}) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [targetUserId, setTargetUserId] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);

  const isAdmin = ["admin", "super"].includes(String(currentUser?.profile || ""));
  const flattenedNotes = useMemo(() => flattenNotes(notes), [notes]);

  const loadData = async () => {
    if (!message?.id || !open) {
      return;
    }

    setLoading(true);
    try {
      const requests = [api.get(`/messages/${message.id}/coach-notes`)];
      if (isAdmin) {
        requests.push(api.get("/users/list"));
      }

      const [notesResponse, usersResponse] = await Promise.all(requests);
      setNotes(Array.isArray(notesResponse?.data?.notes) ? notesResponse.data.notes : []);
      setUsers(Array.isArray(usersResponse?.data) ? usersResponse.data : []);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setNoteText("");
      setReplyTarget(null);
      setTargetUserId("");
      return;
    }

    loadData();
  }, [open, message?.id]);

  useEffect(() => {
    if (!open || replyTarget?.id) {
      return;
    }

    if (!isAdmin) {
      setTargetUserId("");
      return;
    }

    const assignedUserId = Number(ticket?.userId || message?.ticket?.userId || 0);
    setTargetUserId(assignedUserId > 0 ? String(assignedUserId) : "");
  }, [open, isAdmin, replyTarget?.id, ticket?.userId, message?.ticket?.userId]);

  const handleSave = async () => {
    if (!message?.id || !noteText.trim()) {
      return;
    }

    setSaving(true);
    try {
      await api.post(`/messages/${message.id}/coach-notes`, {
        note: noteText.trim(),
        targetUserId: targetUserId || undefined,
        parentNoteId: replyTarget?.id || undefined,
        kind: replyTarget ? "reply" : "guidance"
      });

      setNoteText("");
      setReplyTarget(null);
      if (!replyTarget?.id && isAdmin) {
        const assignedUserId = Number(ticket?.userId || message?.ticket?.userId || 0);
        setTargetUserId(assignedUserId > 0 ? String(assignedUserId) : "");
      }
      await loadData();
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkRead = async noteId => {
    try {
      await api.post(`/message-coach-notes/${noteId}/read`);
      await loadData();
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      toastError(error);
    }
  };

  const handleResolve = async noteId => {
    try {
      await api.post(`/message-coach-notes/${noteId}/resolve`);
      await loadData();
      if (onChanged) {
        onChanged();
      }
    } catch (error) {
      toastError(error);
    }
  };

  const prepareReply = note => {
    setReplyTarget(note);
    setTargetUserId(String(note.authorUserId || ""));
    const textarea = document.getElementById("message-coach-note-text");
    if (textarea) {
      textarea.focus();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>Anotacion interna del mensaje</DialogTitle>
      <DialogContent dividers>
        <div className={classes.messageBox}>
          <Typography variant="subtitle2" gutterBottom>
            Mensaje base
          </Typography>
          <Typography variant="body2">
            {getMessagePreview(message)}
          </Typography>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <CircularProgress size={28} />
          </div>
        ) : flattenedNotes.length ? (
          flattenedNotes.map(note => {
            const isPendingForMe =
              Number(note.targetUserId || 0) === Number(currentUser?.id || 0) &&
              !note.readAt &&
              note.status !== "resolved";

            return (
              <div
                key={note.id}
                className={`${classes.noteRow} ${note.level > 0 ? classes.noteReply : ""}`}
              >
                <div className={classes.noteHeader}>
                  <div>
                    <Typography variant="subtitle2">
                      {note.authorUser?.name || "Usuario"}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(note.createdAt).toLocaleString("es-CO")}
                    </Typography>
                  </div>
                  <div className={classes.noteMeta}>
                    {note.targetUser?.name ? (
                      <Chip size="small" label={`Para: ${note.targetUser.name}`} />
                    ) : null}
                    <Chip
                      size="small"
                      color={note.status === "resolved" ? "primary" : "default"}
                      label={note.status === "resolved" ? "Resuelta" : "Abierta"}
                    />
                    {isPendingForMe ? (
                      <Chip size="small" style={{ background: "#ffd54f" }} label="Pendiente para mi" />
                    ) : null}
                  </div>
                </div>
                <Typography variant="body2">{note.note}</Typography>
                <div className={classes.actionsRow}>
                  <div>
                    <Button size="small" color="primary" onClick={() => prepareReply(note)}>
                      Responder
                    </Button>
                    {!note.readAt ? (
                      <Button size="small" color="default" onClick={() => handleMarkRead(note.id)}>
                        Marcar leida
                      </Button>
                    ) : null}
                    {note.status !== "resolved" ? (
                      <Button size="small" color="secondary" onClick={() => handleResolve(note.id)}>
                        Resolver
                      </Button>
                    ) : null}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    {note.readAt ? `Leida: ${new Date(note.readAt).toLocaleString("es-CO")}` : "Sin lectura"}
                  </Typography>
                </div>
              </div>
            );
          })
        ) : (
          <Typography variant="body2" className={classes.emptyState}>
            Aun no hay anotaciones internas para este mensaje.
          </Typography>
        )}

        <Divider style={{ margin: "16px 0" }} />

        {replyTarget ? (
          <Chip
            color="primary"
            label={`Respondiendo anotacion #${replyTarget.id} de ${replyTarget.authorUser?.name || "usuario"}`}
            onDelete={() => setReplyTarget(null)}
          />
        ) : null}

        <div className={classes.formGrid}>
          <TextField
            id="message-coach-note-text"
            label={replyTarget ? "Respuesta interna" : "Nueva anotacion interna"}
            placeholder="Escriba la guia, correccion o comentario interno"
            multiline
            rowsMin={4}
            variant="outlined"
            value={noteText}
            onChange={event => setNoteText(event.target.value)}
            fullWidth
          />

          <FormControl variant="outlined" fullWidth disabled={!isAdmin}>
            <InputLabel id="message-coach-target-label">Dirigida a</InputLabel>
            <Select
              labelId="message-coach-target-label"
              value={targetUserId}
              onChange={event => setTargetUserId(event.target.value)}
              label="Dirigida a"
            >
              <MenuItem value="">
                <em>Sin usuario especifico</em>
              </MenuItem>
              {users.map(userItem => (
                <MenuItem key={userItem.id} value={String(userItem.id)}>
                  {userItem.name}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
              {replyTarget
                ? "La respuesta volvera al autor de la anotacion seleccionada."
                : targetUserId
                  ? "Solo el usuario dirigido y administracion veran esta anotacion."
                  : "Si no elige un responsable, la anotacion puede no generar alerta individual en el chat esperado."}
            </Typography>
          </FormControl>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Cerrar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving || !noteText.trim()}
        >
          {saving ? "Guardando..." : replyTarget ? "Enviar respuesta" : "Guardar anotacion"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

MessageCoachNotesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  message: PropTypes.object,
  ticket: PropTypes.object,
  currentUser: PropTypes.object,
  onChanged: PropTypes.func
};

export default MessageCoachNotesDialog;
